import { Checkbox, Dropdown, IDropdownOption, Separator, Stack, Text, TextField, useTheme } from "@fluentui/react";
import { RootState } from "app/store";
import { createTierLevelFactory, reconcileTiers } from "global/Application";
import * as IXrmTypes from "model/CRM";
import * as FAM from "model/FlexibleAttributeModel";
import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";

export interface ITierWizardValue {
    tiers: FAM.ITierLevel[] | null;
    tierSet: IXrmTypes.bcbs_config_tierset | null;
}

export interface ITierChange extends ITierWizardValue {
    tierChanges: ITierLevelChange[];
}

export interface ITierLevelChange {
    tier: FAM.ITierLevel;
    isDelete: boolean;
    oldTier?: FAM.ITierLevel;
    oldTierOptions?: FAM.ITierLevel[];
    newTier?: FAM.ITierLevel;
    isNew?: boolean;
    benefit?: FAM.IBenefit
}

interface IProps extends TComponentConnectedProps {
    disabled?: boolean;
    variant: 'change' | 'new' | 'new-opportunity-option';
    onChange?: (value: ITierChange) => void;
    defaultValue?: ITierWizardValue;
    benefit?: FAM.IBenefit;
    emitOnChangeForDefault?: boolean;
}

const TierWizard = (props: IProps) => {

    const theme = useTheme();
    const [tierSet, setTierSet] = useState<IXrmTypes.bcbs_config_tierset | null>(null);
    const [tierLevels, setTierLevels] = useState<FAM.ITierLevel[] | null>(null);
    const [tiersMerged, setTiersMerged] = useState<ITierLevelChange[] | null>(null);

    useEffect(() => {

        // any default tierSet
        if (props.defaultValue?.tierSet) {
            const tierSet = props.defaultValue.tierSet;
            setTierSet(tierSet);

            let tiers: FAM.ITierLevel[] = [];

            // if tiers were provided, use them otherwise generate new tiers
            if (props.defaultValue?.tiers) {
                tiers = props.defaultValue?.tiers;
            } else {
                // generating levels based on tierset, since it wasn't provided
                const tierSetLevels = props?.state?.data.config?.tierlevels?.filter(t => t._bcbs_tiersetid_value === tierSet?.bcbs_config_tiersetid);
                tiers = tierSetLevels?.map((eachTier, index) => {
                    return createTierLevelFactory(
                        eachTier,
                        undefined,
                        {
                            keep_bcbs_name: props.variant === 'new-opportunity-option' ? true : false
                        }
                    );
                });
            }

            if (tiers.length > 0) {
                setTierLevels(tiers);
            }

            // everything is set, lets emit the change
            if (tierSet != null && tiers.length > 0)
                if (props.emitOnChangeForDefault) {
                    onTierLevelUpdate(tiers, tierSet);
                }

        }

    }, []);

    useEffect(() => {

        const t: ITierChange = {
            tierChanges: tiersMerged ?? [],
            tiers: tierLevels ?? [],
            tierSet: tierSet,
        };

        if (tiersMerged && tiersMerged.length > 0)
            props.onChange?.(t);

    }, [tiersMerged])

    const tierSetOptions: IDropdownOption<IXrmTypes.bcbs_config_tierset>[] = useMemo(() => {
        const isDentalBenefit = props.state.data.config.cache.map_bcbs_config_category_named['Dental'].bcbs_config_categoryid === props.benefit?.bcbs_config_categoryid;


        const options: IDropdownOption<IXrmTypes.bcbs_config_tierset>[] = [];

        props.state?.data.config?.tiersets?.forEach((tier) => {

            // for dental we only show Standard PPO
            if (isDentalBenefit)
                if (!(tier.bcbs_name === "Standard PPO"))
                    return;

            options.push({
                key: tier.bcbs_config_tiersetid!,
                text: tier.bcbs_name!,
                data: tier,
            });

        });

        return options;
    }, []);

    const styleTierLevel = useMemo(() => {
        return {
            root: {
                boxShadow: theme.effects.elevation4,
                padding: '10px 10px 20px 10px',
                margin: '10px 0px 0px 0px',
                backgroundColor: theme.semanticColors.menuBackground
            },
        }
    }, []);

    const onTierLevelUpdate = (newTiers: FAM.ITierLevel[], tierSet?: IXrmTypes.bcbs_config_tierset) => {
        if (tierSet)
            setTierSet(tierSet);

        setTierLevels(newTiers);
        let oldTiers: FAM.ITierLevel[] = [];

        if (props.variant === 'change')
            oldTiers = props.defaultValue?.tiers ?? [];

        const changeOrder = reconcileTiers(oldTiers, newTiers);

        setTiersMerged(changeOrder);
    }

    const onRenderNew = () => {
        return <div>
            <Stack>
                <Dropdown
                    label='Tier Set'
                    options={tierSetOptions}
                    disabled={props.disabled}
                    selectedKey={tierSet?.bcbs_config_tiersetid}
                    onChange={(event: React.FormEvent<HTMLDivElement> | null, option?: IDropdownOption<IXrmTypes.bcbs_config_tierset> | undefined, index?: number | undefined) => {
                        if (option && option.data) {

                            const tierSet: IXrmTypes.bcbs_config_tierset = option.data;

                            const tiersSetLevels = props?.state?.data.config?.tierlevels?.filter(t => t._bcbs_tiersetid_value === tierSet?.bcbs_config_tiersetid);

                            const tiers = tiersSetLevels?.map((eachTier, index) => {
                                return createTierLevelFactory(
                                    eachTier,
                                    undefined,
                                    {
                                        keep_bcbs_name: props.variant === 'new-opportunity-option' ? true : false
                                    }
                                );
                            });

                            onTierLevelUpdate(tiers, tierSet);
                        }

                    }}
                />


                <Stack >
                    {tierSet && tierLevels && tierLevels.map((level, index) => (
                        <div key={level.bcbs_config_tierlevelid}>
                            <Stack styles={styleTierLevel}>
                                <Text variant="xSmall" style={{ color: '#0078D7' }}>{level.bcbs_name}</Text>
                                <Stack horizontal horizontalAlign='space-evenly' >
                                    <TextField
                                        label={"Name"}
                                        styles={{ root: { minWidth: '180px' } }}
                                        disabled={level.isLabelDisabled}
                                        onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string | undefined) => {
                                            const t = [...tierLevels];
                                            t.find(t => t === level)!.name = newValue ?? "";
                                            onTierLevelUpdate(t);
                                        }}
                                        value={level.name}
                                    />
                                    <TextField
                                        label='Description'
                                        value={level.description ?? ''}
                                        disabled={level.isLabelDisabled}
                                        onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string | undefined) => {
                                            const t = [...tierLevels];
                                            t.find(t => t === level)!.description = newValue ?? "";
                                            onTierLevelUpdate(t);
                                        }}
                                    />
                                    <div style={{ marginTop: '10px', gap: '10px' }}>
                                        <Checkbox
                                            label='PCP/Specialist'
                                            checked={level.pcp}
                                            onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
                                                const t = [...tierLevels];
                                                t.find(t => t === level)!.pcp = checked!;
                                                onTierLevelUpdate(t);
                                            }}
                                        />
                                        <Separator />
                                        <Checkbox
                                            label='Out of Network'
                                            checked={level.ion}
                                            disabled={level.isLabelDisabled}
                                            onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
                                                const t = [...tierLevels];
                                                t.find(t => t === level)!.ion = checked!;
                                                onTierLevelUpdate(t);
                                            }}
                                        />
                                    </div>

                                </Stack>
                            </Stack>

                        </div>))
                    }
                </Stack>

            </Stack>
        </div>
    }

    const onRenderChange = () => {
        return (
            <div>
                <Stack>
                    <Dropdown
                        label='Tier Set'
                        options={tierSetOptions}
                        disabled={props.disabled}
                        selectedKey={tierSet?.bcbs_config_tiersetid}
                        onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<IXrmTypes.bcbs_config_tierset> | undefined, index?: number | undefined) => {
                            if (option && option.data) {

                                const _tierSet: IXrmTypes.bcbs_config_tierset = option.data;

                                const tierSetLevels = props?.state?.data.config?.tierlevels?.filter(t => t._bcbs_tiersetid_value === _tierSet?.bcbs_config_tiersetid);

                                const tiers = tierSetLevels?.map((tier, index) => {

                                    let mappedTier: FAM.ITierLevel | undefined = undefined;

                                    // lets see if 
                                    if (props?.defaultValue?.tiers)
                                        mappedTier = props.defaultValue.tiers.find(q => q.bcbs_name === tier.bcbs_name);

                                    return createTierLevelFactory(tier, mappedTier);
                                });


                                onTierLevelUpdate(tiers, _tierSet);
                            }

                        }}
                    />
                    {/* {tierSet && tierLevels && <Separator />} */}

                    <Stack >
                        {tierSet && tiersMerged && tiersMerged.map((eachMap, index) => {

                            const options = eachMap.oldTierOptions?.map(q => {
                                const t: IDropdownOption<FAM.ITierLevel> = {
                                    key: q.bcbs_name!,
                                    text: q.bcbs_name!,
                                    data: q
                                }

                                return t;
                            }) ?? [];

                            return <div key={eachMap.tier.bcbs_config_tierlevelid}>
                                <Stack styles={styleTierLevel}>
                                    <Text variant="small" style={{ color: '#0078D7' }}>{eachMap.tier.bcbs_name}</Text>

                                    <Separator />

                                    <Stack horizontal horizontalAlign='space-evenly' >
                                        <TextField
                                            label={"Name"}
                                            styles={{ root: { minWidth: '180px' } }}
                                            disabled={eachMap.tier.isLabelDisabled || eachMap.isDelete}
                                            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string | undefined) => {
                                                setTiersMerged(t => {
                                                    const _t = [...t!];
                                                    _t[index!].tier!.name = newValue ?? '';
                                                    return _t;
                                                });
                                            }}
                                            value={eachMap.tier.name}
                                        />
                                        <TextField
                                            label='Description'
                                            value={eachMap.tier?.description ?? ''}
                                            disabled={eachMap.tier?.isLabelDisabled || eachMap.isDelete}
                                            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string | undefined) => {
                                                setTiersMerged(t => {
                                                    const _t = [...t!];
                                                    _t[index!].tier!.description = newValue ?? '';
                                                    return _t;
                                                });
                                            }}
                                        />
                                        <div style={{ marginTop: '10px', gap: '10px' }}>
                                            <Checkbox
                                                label='PCP/Specialist'
                                                checked={eachMap.tier.pcp}
                                                disabled={eachMap.isDelete}
                                                onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
                                                    setTiersMerged(t => {
                                                        const _t = [...t!];
                                                        _t[index!].tier!.pcp = checked ?? false;
                                                        return _t;
                                                    });
                                                }}
                                            />
                                            <Separator />
                                            <Checkbox
                                                label='Out of Network'
                                                checked={eachMap.tier?.ion}
                                                disabled={eachMap.tier?.isLabelDisabled || eachMap.isDelete}
                                                onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, checked?: boolean | undefined) => {
                                                    setTiersMerged(t => {
                                                        const _t = [...t!];
                                                        _t[index!].tier!.ion = checked ?? false;
                                                        return _t;
                                                    });
                                                }}
                                            />
                                        </div>

                                    </Stack>

                                    {(eachMap.oldTierOptions?.length ?? 0) > 0 &&
                                        <>
                                            <Separator />

                                            <Dropdown
                                                label="Map"
                                                selectedKey={eachMap.oldTier?.bcbs_name ?? undefined}
                                                // eslint-disable-next-line react/jsx-no-bind
                                                options={options}
                                                onChange={(event, option: IDropdownOption<FAM.ITierLevel> | undefined) => {
                                                    setTiersMerged(t => {
                                                        debugger;
                                                        const _t = [...t!];
                                                        _t[index!].oldTier = option?.data;

                                                        return _t;
                                                    })

                                                }}
                                            />
                                        </>
                                    }
                                    <Separator />
                                    <Stack tokens={{ childrenGap: '2px' }}>
                                        {eachMap.isNew &&
                                            <Text variant="xSmall">⚪ Add</Text>
                                        }
                                        {eachMap.newTier && eachMap.oldTier &&
                                            <Text variant="xSmall">🟢 Mapped from {eachMap.oldTier.name} ({eachMap.oldTier.bcbs_name})</Text>
                                        }
                                        {eachMap.isDelete &&
                                            <Text variant="xSmall">🔴 Delete</Text>
                                        }
                                    </Stack>
                                </Stack>
                            </div>;
                        })
                        }
                    </Stack>

                </Stack>
            </div>
        );
    }

    if (props.variant === 'new' || props.variant === 'new-opportunity-option')
        return onRenderNew();
    else
        return onRenderChange();
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(TierWizard);